/**
 * LocalStorage keys used throughout the application
 * Centralized to avoid magic strings and ensure consistency
 *
 * All filter keys are now scoped by business unit to allow:
 * - Switching between BUs without losing filters
 * - Each BU maintains independent filter state
 * - No filter reset on BU change (unless TTL expired)
 */

/**
 * Filter type constants
 */
export const FILTER_TYPES = {
  LOCATIONS: 'locations',
  DIVISION: 'division',
} as const;

export type FilterType = (typeof FILTER_TYPES)[keyof typeof FILTER_TYPES];

/**
 * Generate business-unit-scoped universal filter key
 * Format: universal:{businessUnitId}:locations
 */
export function getUniversalFilterKey(
  businessUnitId: string,
  filterType: FilterType,
): string {
  return `universal:${businessUnitId}:${filterType}`;
}

/**
 * Generate business-unit-scoped session universal filter key
 * Format: session:universal:{businessUnitId}:locations
 */
export function getSessionUniversalFilterKey(
  businessUnitId: string,
  filterType: FilterType,
): string {
  return `session:universal:${businessUnitId}:${filterType}`;
}

/**
 * Generate business-unit-scoped page filter key
 * Format: filters:{businessUnitId}:audits
 */
export function getPageFilterKey(businessUnitId: string, page: string): string {
  return `filters:${businessUnitId}:${page}`;
}

/**
 * Generate business-unit-scoped activity key
 * Format: filter:{businessUnitId}:lastActivity
 */
export function getActivityKey(businessUnitId: string): string {
  return `filter:${businessUnitId}:lastActivity`;
}

/**
 * Legacy universal filter keys (deprecated - use getUniversalFilterKey instead)
 * Kept for backward compatibility during migration
 */
export const UNIVERSAL_FILTER_KEYS = {
  LOCATIONS: 'universal:locations',
  DIVISION: 'universal:division',
} as const;

/**
 * Page identifiers for filter keys
 * Use with getPageFilterKey(businessUnitId, PAGE_IDENTIFIERS.AUDITS)
 */
export const PAGE_IDENTIFIERS = {
  AUDITS: 'audits',
  TASKS: 'tasks',
  QUESTIONS: 'questions',
  TEMPLATES: 'templates',
  CATEGORIES: 'categories',
  TAGS: 'tags',
  USERS: 'users',
  TEAMS: 'teams',
  SCHEDULES: 'schedules',
  LOCATIONS: 'locations',
} as const;

/**
 * Legacy page-specific filter keys (deprecated - use getPageFilterKey instead)
 * Kept for backward compatibility during migration
 */
export const PAGE_FILTER_KEYS = {
  AUDITS: 'filters:audits',
  TASKS: 'filters:tasks',
  QUESTIONS: 'filters:questions',
  TEMPLATES: 'filters:templates',
  CATEGORIES: 'filters:categories',
  TAGS: 'filters:tags',
  USERS: 'filters:users',
  TEAMS: 'filters:teams',
  SCHEDULES: 'filters:schedules',
  LOCATIONS: 'filters:locations',
} as const;

/**
 * Filter activity and metadata keys
 * Used for TTL tracking and current business unit tracking
 */
export const FILTER_METADATA_KEYS = {
  LAST_ACTIVITY: 'filter:lastActivity', // Legacy - use getActivityKey instead
  CURRENT_BUSINESS_UNIT: 'filter:currentBusinessUnit',
} as const;

/**
 * Storage key prefixes for pattern matching
 * Used in utility functions like clearAllFilters()
 */
export const STORAGE_KEY_PREFIXES = {
  FILTERS: 'filters:',
  FILTER: 'filter:',
  UNIVERSAL: 'universal:',
  SESSION_UNIVERSAL: 'session:universal:',
} as const;

/**
 * Generate key for CI project modal "shown" state
 * Format: ci-modal:{auditId}:{userId}
 */
export function getCIModalStorageKey(auditId: string, userId: string): string {
  return `ci-modal:${auditId}:${userId}`;
}
